import React, { useContext, useEffect, useState } from 'react';
import { Box, TextField, Button, Typography } from '@mui/material';
import Parser from 'html-react-parser';
import { RegistrationContext } from './RegistrationContainer';
import { StoreContext } from '../../components/Store';
import { sendVerificationCode } from '../../api/registration';

const BeforeYouStart = () => {
  const { formData, setFormData, error, setError, handleChange, handleBlur, setActiveStep, isValidEmail } =
    useContext(RegistrationContext);
  const { ssoUserDetails, isSSOUserWithNoCompany } = useContext(StoreContext);

  const userDetails = ssoUserDetails[0];

  useEffect(() => {
    if (userDetails?.username) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        email: userDetails.username,
        contactName: userDetails.name || '',
      }));
    }
  }, [userDetails, setFormData]);

  const handleNext = async () => {
    if (!formData.email) {
      setError('Email is required.');
      return;
    }
    if (!isValidEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      const skipVerification = isSSOUserWithNoCompany[0];
      const data = skipVerification ? {} : await sendVerificationCode(formData.email);

      if (data.error && !skipVerification) {
        // validation error - user likely already exists
        setError(data.error);
      } else {
        setError('');
        setFormData((prevFormData) => ({
          ...prevFormData,
          contactEmail: prevFormData.email,
        }));
        setActiveStep((prevStep) => prevStep + 1);
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      setError(error.message);
    }
  };

  return (
    <Box>
      {ssoUserDetails[0]?.name && (
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          <strong>There is currently no company associated with your account. Please register to continue.</strong>
        </Typography>
      )}
      <Typography variant="body1" gutterBottom>
        This subscription service will allow you to make EU Safety and Security declarations (known as ICS2) to Customs
        for movements to the European Union.
      </Typography>
      <Typography variant="body1" gutterBottom>
        You must ensure you have the carrier’s ‘knowledge and consent’ to submit the entry summary declaration on their
        behalf.
      </Typography>
      <Typography variant="body1" gutterBottom>
        You will be asked to provide information about your business to set up an account for commercial use, which
        include a credit check. It is important that you are authorised to do this on behalf of your business or
        employer.
      </Typography>
      <Typography variant="body1" gutterBottom>
        As a guide, you will require the following information to complete this stage of the application process.
      </Typography>
      <Typography variant="body1" gutterBottom>
        <ul>
          <li>Company Name</li>
          <li>EU EORI number</li>
          <li>Address</li>
          <li>Business contact details</li>
          <li>Payment details</li>
        </ul>
      </Typography>
      <Typography variant="body1" gutterBottom>
        As part of this sign up process you will need to complete an Account Application and Terms and Conditions via
        DocuSign.
      </Typography>
      <Typography variant="body1" gutterBottom>
        Your application will be reviewed within 72 hours.
      </Typography>
      <Typography variant="body1" gutterBottom>
        To register for this service, please enter your email address to receive a verification code (valid for 1 hour).
      </Typography>
      <TextField
        fullWidth
        label="Email Address"
        variant="outlined"
        name="y-email"
        value={formData.email}
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ my: 2 }}
        error={!!error}
        autoComplete="new-email"
        disabled={isSSOUserWithNoCompany[0]}
        slotProps={{
          autoComplete: 'new-email',
          form: 'off',
          inputLabel: {
            shrink: true,
          },
        }}
        helperText={
          error ? (
            <Typography component="span" variant="caption" color="error">
              {Parser(error)}
            </Typography>
          ) : null
        }
      />
      <Button variant="contained" onClick={handleNext}>
        Sign Me Up
      </Button>
    </Box>
  );
};

export default BeforeYouStart;
